import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import type {
  PCPSameAsReferrer,
  ProviderDetails,
  ProviderSkipped,
} from "@/api/generated/models";
import {
  Button,
  Checkbox,
  Footer,
  Header,
  Heading,
  Paragraph,
  ScrollView,
  YStack,
} from "@/components";
import {
  EMPTY_PROVIDER,
  ProviderFields,
  providerPayload,
  providerSchema,
  providerValues,
} from "./CareTeamProviderFields";

export const PCPCollectionScreen = ({
  initialAnswer,
  referrer,
  onContinue,
  isPending = false,
}: {
  initialAnswer?: ProviderDetails | ProviderSkipped | PCPSameAsReferrer;
  referrer: ProviderDetails | ProviderSkipped;
  onContinue: (
    answer: ProviderDetails | ProviderSkipped | PCPSameAsReferrer,
  ) => Promise<void>;
  isPending?: boolean;
}) => {
  const [sameAsReferrer, setSameAsReferrer] = useState(
    initialAnswer?.status === "same_as_referrer",
  );
  const restored = useMemo(
    () =>
      initialAnswer?.status === "provided"
        ? providerValues(initialAnswer)
        : undefined,
    [initialAnswer],
  );
  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(providerSchema),
    defaultValues: EMPTY_PROVIDER,
    values: restored,
    resetOptions: { keepDirtyValues: true },
  });

  useEffect(() => {
    if (referrer.status !== "provided") setSameAsReferrer(false);
  }, [referrer.status]);

  const useReferrer = sameAsReferrer && referrer.status === "provided";
  const disabled = isPending || isSubmitting;
  const continuePress = useReferrer
    ? () => onContinue({ status: "same_as_referrer" })
    : handleSubmit((values) => onContinue(providerPayload(values)));

  return (
    <>
      <ScrollView
        flex={1}
        pointerEvents={disabled ? "none" : "auto"}
        contentContainerStyle={{ padding: "$200", gap: "$250" }}
      >
        <Header gap="$100">
          <Heading size="$xl">Your primary care provider</Heading>
          <Paragraph size="$sm">
            Enter your primary care provider’s details below.
          </Paragraph>
        </Header>
        {referrer.status === "provided" && (
          <Checkbox
            id="pcp-same-as-referrer"
            checked={sameAsReferrer}
            onCheckedChange={(checked) => setSameAsReferrer(checked === true)}
            label="My PCP is the same as my referring clinician"
          />
        )}
        <YStack gap="$250">
          <ProviderFields
            control={control}
            setValue={setValue}
            errors={errors}
            role="pcp"
            readOnlyValues={useReferrer ? providerValues(referrer) : undefined}
          />
        </YStack>
      </ScrollView>
      <Footer padding="$200" mode="dialog">
        <Button
          dataTrackingId="access-pcp-continue-button"
          mode="primary"
          constrained
          onPress={continuePress}
          disabled={disabled}
          loading={disabled}
        >
          Continue
        </Button>
        <Button
          dataTrackingId="access-pcp-skip-button"
          mode="tertiary"
          onPress={() => onContinue({ status: "skipped" })}
          disabled={disabled}
        >
          Skip for now
        </Button>
      </Footer>
    </>
  );
};
